import type {
  TranscriptionApiToken,
  TranscriptionApiTranscription,
  TranscriptionStateCall,
  TranscriptionStateToken,
  TranscriptVersion,
} from './transcriptionSlice.types';

/**
 * A call that starts > 10 minutes after the transcript starts is considered a new call.
 * (Used by selectors to differentiate "previous transcripts".)
 */
export const MAX_CALL_START_TIME_OFFSET = 10 * 60 * 1000;

export const UNKNOWN_SPEAKER = 'Unidentified Speaker';

export const TRANSCRIPT_VERSIONS: TranscriptVersion[] = [
  'original',
  'translated',
];

/** The index into state.transcriptions.calls[callerId].summaries that indicates the latest summary */
export const LATEST_SUMMARY_INDEX = 0;

export const addLanguage = (languages: string[] = [], language: string) => {
  if (language && !languages.includes(language)) {
    return [...languages, language];
  }
  return languages;
};

export const findTranslatedText = (token: TranscriptionApiToken) => {
  let translated = '';
  // TODO: REVIEW: Seems this function assumes UI always prefers English. Correct?
  if (token?.translated_transcript?.en) {
    translated = token.translated_transcript.en;
  } else if (token?.translated_transcript?.transcript) {
    translated = token.translated_transcript.transcript;
  }

  return translated;
};

export function apiTranscriptionToStateCall(
  api: TranscriptionApiTranscription | null | undefined,
): TranscriptionStateCall {
  const safe: TranscriptionApiTranscription = api || {
    audio_data: { status: 'pending' },
    speakers: [],
    transcription: {},
    tokens: [],
    summaries: [],
    call_information: {
      call_start_time: 0,
      call_end_time: 0,
      caller_id: '',
      digi_call_id: '',
    },
  };

  const callInformation = safe?.call_information || {
    call_start_time: 0,
    call_end_time: 0,
    caller_id: '',
    digi_call_id: '',
  };

  // Sort tokens by start_time to ensure correct order of the conversation
  const sortedTokens = [...(safe.tokens || [])].sort(
    (a, b) => a.start_time - b.start_time,
  );

  // Sort summaries by generated_time descending
  const sortedSummaries = [...(safe.summaries || [])].sort(
    (a, b) =>
      new Date(b.generated_time).getTime() -
      new Date(a.generated_time).getTime(),
  );

  // Add default values to summaries
  const normalizedSummaries = sortedSummaries.map(summary => ({
    ...summary,
    summary: summary.summary || '',
    keywords: summary.keywords || [],
    keyword_matches: summary.keyword_matches || [],
  }));

  let languagesDetected: string[] = [];

  // Format each token to include speaker and translations if available
  const enrichedTokens = sortedTokens.map(token => {
    const enrichedToken: TranscriptionStateToken = {
      ...token,
      speaker_name: UNKNOWN_SPEAKER,
      speaker_role: UNKNOWN_SPEAKER,
      speaker_display_name: UNKNOWN_SPEAKER,
    };

    if (enrichedToken.detected_language !== 'en') {
      languagesDetected = addLanguage(
        languagesDetected,
        enrichedToken.detected_language,
      );
      const translated = `${findTranslatedText(token)}`.trim();
      if (translated) {
        enrichedToken.translated = translated;
      }
    }

    const speaker = (safe.speakers || []).find(s => s.id === token.speaker_id);
    if (speaker) {
      enrichedToken.speaker_name = speaker?.name;
      enrichedToken.speaker_role = speaker?.role;
      enrichedToken.speaker_display_name = speaker?.display_name;
    }

    return enrichedToken;
  });

  return {
    fetching: false,
    error: null,
    payload: {
      tokens: enrichedTokens,
      speakers: safe.speakers || [],
      audio_data: safe.audio_data || { status: 'pending' },
      languagesDetected,
      call_information: callInformation,
    },
    summaries: normalizedSummaries || [],
    keywords: normalizedSummaries[LATEST_SUMMARY_INDEX]?.keywords || [],
    keyword_matches:
      normalizedSummaries[LATEST_SUMMARY_INDEX]?.keyword_matches || [],
    selectedKeywordMatch: null,
    manualLanguage: null,
    languageChangeEvents: [],
    lastShownManualLanguage: undefined,
    hasShownAutoDetection: false,
  };
}
